import { makeObservable, action, observable } from 'mobx';
import AppRequests from 'Utils/Requests/AppRequests';
import IntegrationSettingsStore from 'Stores/integrationSettingsStore';
import ConverterSettingsStore from 'Stores/converterSettingsStore';
import OtherSettingsStore from 'Stores/otherSettingsStore';
import UserSettingsStore from 'Stores/userSettingsStore';
import UsersStore from 'Stores/usersStore';
import UsersSyncStore from 'Stores/UsersSyncStore';
import ProtocolRecordsStore from 'Stores/ProtocolRecords/protocolRecordsStore';
import AlertsHolderStore from 'Stores/alertsHolderStore';
import MigrationStore from 'Stores/migrationStore';
import { InitModal } from 'Utils/CommonFunctions';
import DocumentReloadStore from 'Stores/DocumentReload/documentReloadStore';
import TracesStore from 'Stores/tracesStore';
import Cookies from 'js-cookie';
import TasksStore from 'Stores/tasksStore';

class AppStore {
    leftPanel;
    loggedIn;
    pageTitle;
    pageTitleAdditionalInfo;
    selectedPage;
    firstPingDone;
    smrWorkState;
    dmsisIsWork;
    /** @type {import("Components/Common/modalWindow").ModalWindowProps | null} */
    modalSettings;
    lastPageInfo;
    /** @type {string[]} */
    filterAvailablePagesForCurrentUser;
    productModeUserRights;
    /** @type {ProductModeSettingsValues | null} */
    productModeSettings;
    meetingSwitcherEnabled;
    /** @type {boolean} */
    isMeeting;
    /** @type {boolean} */
    isAllProductModes;
    meetingSwitcherEnabledCurrentUser;
    checkTransitionAllowed;
    changeCookieBroadcast;
    updateProductModesByTimer;
    isOsAuthEnabled;

    constructor() {
        makeObservable(this, {
            leftPanel: observable,
            loggedIn: observable,
            pageTitle: observable,
            pageTitleAdditionalInfo: observable,
            selectedPage: observable,
            firstPingDone: observable,
            smrWorkState: observable,
            dmsisIsWork: observable,
            modalSettings: observable.ref,
            filterAvailablePagesForCurrentUser: observable.ref,
            productModeUserRights: observable.ref,
            productModeSettings: observable.ref,
            meetingSwitcherEnabled: observable,
            isMeeting: observable,
            isAllProductModes: observable,
            meetingSwitcherEnabledCurrentUser: observable,
            isOsAuthEnabled: observable,

            SetLoggedInState: action,
            SetCheckTransitionAllowed: action,
            SetProductModes: action,
        });
        this.modalSettings = null;
        this.leftPanel = {
            integrationServiceMenuItemHasError: false,
            converterMenuItemHasError: false,
            stopListMenuItemHasError: false
        }
        this.loggedIn = false;
        this.smrVersion = window.SmrVersion;
        this.timerId = null;
        this.timerTimeout = 1000;
        this.firstPingDone = false;
        this.pageTitle = 'Неизвестная страница';
        this.pageTitleAdditionalInfo = '';
        this.lastPageInfo = {
            pageTitle: '',
            params: {},
            hash: ''
        };
        this.smrWorkState = {
            doesSmrWork: true,
            stopReason: null
        };
        this.isOsAuthEnabled = false;

        this.dmsisIsWork = true;
        this.productModeSettings = null;
        this.productModeUserRights = null;
        this.filterAvailablePagesForCurrentUser = null;
        this.headerProductModeSwitcherVisiblePageNames = [
            "users",
            "userSettings",
            // TODO: пока скрываем свитчер, см. Task 156596.
            // "mkMessageList",
            // "newMessage",
            "stopList",
            "protocolRecords",
            "documentHistory",
            "documentReload"
        ];
        this.meetingSwitcherEnabledCheckPageNames = [
            "userSettings"
        ];
        this.headerProductModeSwitcherButtonAllVisiblePageNames = [
            "protocolRecords"
        ];
        this.meetingSwitcherEnabledCurrentUser = false;
        this.meetingSwitcherEnabled = false;
        this.funcCurrentPageReloadData = null;
        this.checkTransitionAllowed = () => Promise.resolve(true);

        this.updateProductModesByTimer = false;
        try {
            window.cookieStore.addEventListener('change', () => this.UpdateProductModes());
        }
        catch {
            try {
                this.changeCookieBroadcast = new BroadcastChannel('smr_cookie_FD5BA5A15807');
                this.changeCookieBroadcast.onmessage = () => this.UpdateProductModes();
            }
            catch {
                this.updateProductModesByTimer = true;
            }
        }
        this.UpdateProductModes();

        InitModal(this.ShowModal.bind(this), this.CloseModal.bind(this));
    }

    ShowModal = action((/** @type {import('Components/Common/modalWindow').ModalWindowProps} */ settings) => {
        this.modalSettings = settings;
    });

    CloseModal = action(() => {
        this.modalSettings = null;
    });

    OnMainComponentMount = action((title, pageName, funcReloadData) => {
        this.pageTitle = title;
        this.pageTitleAdditionalInfo = '';
        document.title = `${title} - Сервер Мобильных Решений`;

        this.selectedPage = pageName;

        this.funcCurrentPageReloadData = funcReloadData;

        //AlertsHolderStore.CloseAllAlerts(); // не закрывать alert после завершения миграции . DefaultCollection - BUG 167293
        AlertsHolderStore.ChangeArchiveOpenState(false);

        this.CloseModal();
    });

    SetLastPageInfo = action((info) => {
        this.lastPageInfo = info;
    });

    PrintSmrStartError() {
        AppRequests.PrintSmrStartError();
    }

    ClearUserRightsInfo() {
        this.productModeUserRights = null;
        this.filterAvailablePagesForCurrentUser = null;
    }

    GetServiceStateBeforeLogin() {
        AppRequests.GetServiceStateBeforeLogin()
            .then(action((values) => {
                this.isOsAuthEnabled = values.isOsAuthEnabled;
            }))
            .catch(e => {
                this.SetLoggedInState(false);
            });
    }

    GetServiceState() {
        AppRequests.GetServiceState()
            .then(action((values) => {
                this.smrWorkState = values.smrWorkStatus;
                this.SetLoggedInState(!values.needToReLogin);

                this.productModeSettings = values.productModeSettings;
                this.productModeUserRights = values.productModeUserRights;
                this.filterAvailablePagesForCurrentUser = values.productModeUserRights.filterAvailablePagesForCurrentUser;

                MigrationStore.SetMigrationState(values.migrationStatus);
                this.firstPingDone = true;

                this.meetingSwitcherEnabled = values.meetingSwitcherEnabled;
                this.SetProductModes(values.isMeeting, values.isAllProductModes);

                this.leftPanel = values.leftPanelState;

                IntegrationSettingsStore.SetDmsisState(values.dmsisStatus);
                this.dmsisIsWork = values.dmsisStatus.hasError != true;
                IntegrationSettingsStore.SetCarmaState(values.carmaStatus);
                ConverterSettingsStore.SetConverterState(values.converterStatus);
                ConverterSettingsStore.SetDeloConverterState(values.deloConverterStatus);
                ConverterSettingsStore.SetFileStorageState(values.freeSpaceStatus);
                UsersSyncStore.SetState(values.usersState, values.usersReloadState);

                //todo убрать: usersState, usersReloadState
                UserSettingsStore.SetUserState(values.usersState, values.usersReloadState, values.dmsisStatus);
                UsersStore.SetUsersState(values.usersState, values.usersReloadState);

                ProtocolRecordsStore.SetProtocolState(values.protocolState);
                OtherSettingsStore.SetOtherSettingsState(values.usersReloadState);
                OtherSettingsStore.SetRestrictionUpdateState(values.restrictionUpdateState);
                OtherSettingsStore.SetBigFileUpdateState(values.bigFileUpdateState);
                MigrationStore.SetMigrationState(values.migrationStatus);
                DocumentReloadStore.SetState(values.documentReloadState);
                TracesStore.SetState(values.frontTracesEnabled);
                TasksStore.SetState(values.prepareDownloadFileState);
            }))
            .catch(e => {
                this.SetLoggedInState(false);
            });
    }

    StartTimerWork() {
        clearInterval(this.timerId);
        this.timerId = setInterval(
            () => this.DoTimerWork(),
            this.timerTimeout
        );
    }

    StopTimerWork() {
        clearInterval(this.timerId);
    }

    DoTimerWork() {
        if (this.updateProductModesByTimer)
            this.UpdateProductModes();

        this.GetServiceState();

        UsersStore.UpdateUsersFromTimer();
    }

    /**
     * @param {boolean} state
     */
    SetLoggedInState(state) {
        if (this.loggedIn == state)
            return;

        this.loggedIn = state;

        if (state) {
            this.StartTimerWork();
            this.ClearUserRightsInfo();
        } else {
            this.StopTimerWork();
        }
    }

    IsHeaderProductModeSwitcherVisibleForCurrentPage() {
        return this.headerProductModeSwitcherVisiblePageNames.some(it => it == this.selectedPage);
    }

    IsNeedCheckMeetingSwitcherEnabledCurrentUser() {
        return this.meetingSwitcherEnabledCheckPageNames.some(it => it == this.selectedPage);
    }

    IsHeaderProductModeSwitcherEnabledForCurrentPage() {
        return (
            (
                this.IsNeedCheckMeetingSwitcherEnabledCurrentUser()
                    ? this.meetingSwitcherEnabledCurrentUser == true
                    : true
            )
            &&
            this.meetingSwitcherEnabled == true
        );
    }

    IsHeaderProductModeSwitcherBottonAllVisible() {
        return this.headerProductModeSwitcherButtonAllVisiblePageNames.some(it => it == this.selectedPage);
    }

    GetIsAllProductModes() {
        return this.IsHeaderProductModeSwitcherBottonAllVisible() ? this.isAllProductModes : null;
    }

    /**
     * @param {boolean} isMeeting 
     * @param {boolean} isAllProductModes 
     */
    SaveSettingProductMode(isMeeting, isAllProductModes) {
        this.checkTransitionAllowed()
            .then(result => {
                if (!result)
                    return;

                this.SetProductModes(isMeeting, isAllProductModes);
                this.GetServiceState();
            })
    }

    UpdateProductModes() {
        this.SetProductModes(Cookies.get('IsMeeting') == '1', Cookies.get('IsAllProductModes') == '1');
    }

    /**
     * @param {boolean} isMeeting
     * @param {boolean} isAllProductModes
     */
    SetProductModes(isMeeting, isAllProductModes) {
        if (this.isMeeting == isMeeting && this.isAllProductModes == isAllProductModes)
            return;

        this.isMeeting = isMeeting;
        this.isAllProductModes = isAllProductModes;

        Cookies.set('IsMeeting', this.isMeeting ? '1' : '0');
        Cookies.set('IsAllProductModes', this.isAllProductModes ? '1' : '0');

        this.changeCookieBroadcast?.postMessage('CookiesChanged');

        if (this.funcCurrentPageReloadData)
            this.funcCurrentPageReloadData();
    }

    /**
     * @param {(() => Promise<boolean>) | null} checkTransitionAllowed 
     */
    SetCheckTransitionAllowed(checkTransitionAllowed) {
        if (checkTransitionAllowed == null)
            this.checkTransitionAllowed = () => Promise.resolve(true);
        else
            this.checkTransitionAllowed = checkTransitionAllowed;
    }
}

export default new AppStore();